<script setup lang="ts">
	import { tripReasonLabel } from '@/enums/tripReasons'
	import { computed } from 'vue'

	import useCompanyReportHotelAnalytics from '@/modules/companies/composables/useCompanyReportHotelAnalytics'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = defineProps<Props>()

	const { analytics, isFetching: isLoading } = useCompanyReportHotelAnalytics(
		computed(() => ({
			companyId: props.companyId,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				groupBy: props.groupByFilter,
				travelerId: props.searchTravelerId || undefined,
				hotelId: props.searchHotelId || undefined,
				createdAt: props.createdAt || undefined,
				orderBy: props.orderBy || undefined,
				orderDirection: props.orderDirection || undefined,
			},
		}))
	)

	const metrics = computed(() => {
		if (!analytics.value?.operational) return null
		return analytics.value.operational
	})

	const hasValidMetrics = computed(() => {
		if (!metrics.value) return false

		const cancellations = metrics.value.cancellations
		const repeatHotels = metrics.value.repeatHotels

		const hasCancellationData =
			cancellations &&
			(cancellations.totalReservations > 0 ||
				cancellations.cancelledReservations > 0)

		const hasRepeatHotelsData =
			repeatHotels &&
			(repeatHotels.hotelsBookedOnce > 0 ||
				repeatHotels.hotelsBookedMoreThanOnce > 0)

		const hasTopReasons =
			metrics.value.topReasons && metrics.value.topReasons.length > 0

		return hasCancellationData || hasRepeatHotelsData || hasTopReasons
	})

	// BookingV2 ya agrupa por el motivo del catálogo y traduce lo heredado; aquí
	// sólo se cubre una respuesta vieja que todavía mande el valor en inglés.
	const translateReason = (reason: string) => tripReasonLabel(reason) || reason

	const reasonCategories = computed(() =>
		(metrics.value?.topReasonCategories ?? []).filter(
			(item) => item.category !== 'Sin categoría'
		)
	)

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
			minimumFractionDigits: 0,
		}).format(value)
	}

	// Exponer isLoading para el componente padre
	defineExpose({
		isLoading,
	})
</script>

<template>
	<div class="overflow-hidden rounded-lg bg-white shadow">
		<div class="px-4 py-5 sm:p-6">
			<div
				v-if="isLoading && !metrics"
				class="flex items-center justify-center py-8"
			>
				<div class="text-center">
					<span
						class="material-symbols-outlined mb-2 animate-spin text-4xl text-blue-ef-500"
					>
						progress_activity
					</span>
					<p class="text-sm text-gray-600">Cargando métricas...</p>
				</div>
			</div>

			<div
				v-else-if="metrics && hasValidMetrics"
				class="space-y-6"
			>
				<!-- Primera fila: Cancelaciones y Noches -->
				<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
					<!-- Cancelaciones -->
					<div class="rounded-lg bg-red-50 p-4">
						<div class="flex justify-between items-end">
							<div class="mt-2">
								<div class="flex items-center">
									<span class="material-symbols-outlined text-2xl text-red-500">
										cancel
									</span>
									<p class="ml-2 text-sm font-medium text-red-700">
										Cancelaciones
									</p>
								</div>

								<p class="text-3xl font-bold text-red-600">
									{{ metrics.cancellations?.cancellationRate || 0 }}%
								</p>
								<p class="mt-1 text-xs text-red-600">
									{{ metrics.cancellations?.cancelledReservations || 0 }} de
									{{ metrics.cancellations?.totalReservations || 0 }}
									reservaciones
								</p>
							</div>

							<div class="rounded-lg bg-red-300 p-2 text-center">
								<div class="flex mb-1 items-center justify-center space-x-1">
									<span class="material-symbols-outlined text-2xl text-white">
										payments
									</span>
									<p class="text-xs text-white">Penalizaciones</p>
								</div>

								<p class="text-lg font-semibold text-white">
									{{ metrics.cancellations?.totalPenalties || '$0MXN' }}
								</p>
							</div>
						</div>
					</div>

					<!-- Hoteles repetidos -->
					<div class="rounded-lg bg-purple-50 p-4">
						<div class="flex items-center">
							<span class="material-symbols-outlined text-2xl text-purple-500">
								repeat
							</span>
							<p class="ml-2 text-sm font-medium text-purple-700">
								Hoteles Repetidos
							</p>
						</div>
						<p class="mt-2 text-3xl font-bold text-purple-600">
							{{ metrics.repeatHotels?.repeatRate?.toFixed(1) || 0 }}%
						</p>
						<p class="mt-1 text-xs text-purple-600 hidden sm:block">
							{{ metrics.repeatHotels?.hotelsBookedMoreThanOnce || 0 }} de
							{{ metrics.repeatHotels?.totalHotels || 0 }} hoteles
						</p>
						<p class="mt-1 text-xs text-purple-600 sm:hidden">
							{{ metrics.repeatHotels?.hotelsBookedMoreThanOnce || 0 }}
							repetidos de
							{{ metrics.repeatHotels?.totalHotels || 0 }} hoteles usados
						</p>
					</div>
				</div>

				<!-- Segunda fila: Detalles adicionales -->
				<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<!-- Razones de viaje repetidas -->
					<div class="rounded-lg border border-gray-200 p-4">
						<div class="flex items-center justify-between">
							<div class="flex items-center">
								<span class="material-symbols-outlined text-xl text-gray-500">
									work
								</span>
								<p class="ml-2 text-sm font-medium text-gray-700">
									Razones de Viaje
								</p>
							</div>
							<span
								class="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700"
							>
								{{ metrics.repeatReasons?.repeatRate?.toFixed(0) || 0 }}%
								repetición
							</span>
						</div>
						<div class="mt-3 space-y-2">
							<div
								v-for="reason in metrics.topReasons"
								:key="reason.reason"
								class="flex items-center justify-between rounded bg-gray-50 px-3 py-2"
							>
								<div>
									<p class="text-sm font-medium text-gray-800">
										{{ translateReason(reason.reason) }}
									</p>
									<p class="text-xs text-gray-500">
										{{ reason.reservations }} reservaciones
									</p>
								</div>
								<p class="text-sm font-semibold text-green-600">
									{{ formatCurrency(reason.totalSpend) }}
								</p>
							</div>
							<p
								v-if="!metrics.topReasons?.length"
								class="text-center text-sm text-gray-400"
							>
								Sin datos de razones
							</p>
						</div>
						<div
							v-if="reasonCategories.length"
							class="mt-3 border-t border-gray-100 pt-3"
						>
							<p class="mb-2 text-xs font-medium text-gray-500">
								Por categoría
							</p>
							<div class="flex flex-wrap gap-2">
								<span
									v-for="item in reasonCategories"
									:key="item.category"
									class="rounded-full bg-green-50 px-2.5 py-1 text-xs text-green-700"
								>
									{{ item.category }} · {{ item.reservations }}
								</span>
							</div>
						</div>
					</div>

					<!-- Resumen de hoteles -->
					<div class="rounded-lg border border-gray-200 p-4">
						<div class="flex items-center justify-between">
							<div class="flex items-center">
								<span class="material-symbols-outlined text-xl text-gray-500">
									hotel
								</span>
								<p class="ml-2 text-sm font-medium text-gray-700">
									Resumen de Hoteles
								</p>
							</div>
							<span
								class="rounded-full bg-purple-100 px-2 py-1 text-xs text-purple-700 block md:hidden"
							>
								{{ metrics.repeatHotels?.hotelsBookedMoreThanOnce || 0 }} de
								{{ metrics.repeatHotels?.totalHotels || 0 }} hoteles repetidos
							</span>
						</div>
						<div class="mt-3 space-y-3">
							<div class="flex justify-between">
								<span class="text-sm text-gray-600"
									>Hoteles reservados más de una vez</span
								>
								<span class="font-semibold text-gray-800">
									{{ metrics.repeatHotels?.hotelsBookedMoreThanOnce || 0 }}
								</span>
							</div>

							<div class="flex justify-between">
								<span class="text-sm text-gray-600">
									Reservaciones en hoteles repetidos
								</span>
								<span class="font-semibold text-gray-800">
									{{ metrics.repeatHotels?.reservationsInRepeatHotels || 0 }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				v-else
				class="flex items-center justify-center py-8"
			>
				<div class="text-center">
					<span class="material-symbols-outlined mb-3 text-5xl text-gray-300">
						analytics
					</span>
					<p class="text-base font-medium text-gray-600">
						No hay datos disponibles para esta sección
					</p>
					<p class="mt-2 text-sm text-gray-500">
						No se encontraron métricas operacionales
					</p>
				</div>
			</div>
		</div>
	</div>
</template>
